import { AgentError, AgentOfflineError, fetchDraft, openDraftSocket, saveChanges } from '../clients/agent';
import { applyChanges, draftStateToData, emptyDraftState, MEDIA_FIELD_TYPE, recordDifference } from './apply-change';
import { setLeaveOutboxFlush } from '../utils/on-leave';
import { actions as pageActions } from './slices/page';
import { actions as remoteActions } from './slices/remote';
import { actions as siteActions } from './slices/site';
import * as widgetActions from './slices/widget';
const RETRY_SECONDS = [2, 5, 15, 30];
const MAX_BATCH = 20;
const CALL_LIMIT = 200;
const MAX_WAIT_MS = 2500;
const SOCKET_SAFETY_POLL_MS = 30000;
const goesAtOnce = (change) => {
    if (change.kind !== 'fieldWrite' && change.kind !== 'fieldMerge') {
        return true;
    }
    return change.fieldType === MEDIA_FIELD_TYPE;
};
const recordAction = (action, stateAfter, stateBefore) => {
    const at = new Date().toISOString();
    const payload = action.payload;
    switch (action.type) {
        case 'pages/field_write': {
            const whole = {
                kind: 'fieldWrite',
                pageId: payload.pageId,
                section: payload.section,
                fieldType: payload.fieldType,
                fieldName: payload.fieldName,
                value: payload.value,
                props: payload.props,
                at
            };
            if (payload.fieldType !== MEDIA_FIELD_TYPE || payload.section) {
                return whole;
            }
            const before = stateBefore?.page?.pages?.nodes?.[payload.pageId]?.jaenFields?.[payload.fieldType]?.[payload.fieldName]?.value;
            const difference = recordDifference(before, payload.value);
            if (!difference)
                return whole;
            return {
                kind: 'fieldMerge',
                pageId: payload.pageId,
                fieldType: payload.fieldType,
                fieldName: payload.fieldName,
                value: difference.changed,
                props: { ...(payload.props || {}), removed: difference.removed },
                at
            };
        }
        case 'pages/section_add':
            return {
                kind: 'sectionAdd',
                pageId: payload.pageId,
                section: { path: payload.path },
                props: {
                    sectionItemType: payload.sectionItemType,
                    between: payload.between
                },
                at
            };
        case 'pages/section_remove':
            return {
                kind: 'sectionRemove',
                pageId: payload.pageId,
                section: { path: payload.path, id: payload.sectionId },
                props: { between: payload.between },
                at
            };
        case 'pages/section_move':
            return {
                kind: 'sectionMove',
                pageId: payload.pageId,
                section: { path: payload.path, id: payload.sectionId },
                props: { between: payload.between, move: payload.move },
                at
            };
        case 'pages/page_updateOrCreate': {
            const pageId = payload.id || stateAfter?.page?.pages?.lastAddedNodeId;
            if (!pageId)
                return null;
            return {
                kind: 'pageUpdate',
                pageId,
                value: { ...payload, id: pageId },
                at
            };
        }
        case 'pages/page_markForDeletion':
            return { kind: 'pageDelete', pageId: payload, at };
        case 'site/updateSiteMetadata':
            return { kind: 'siteMetadata', value: payload, at };
        case 'widget/writeData': {
            const nodes = stateAfter?.widget?.nodes || [];
            const written = payload.id
                ? nodes.find((node) => node.id === payload.id)
                : nodes[nodes.length - 1];
            return {
                kind: 'widgetWrite',
                value: written
                    ? { ...written, isCreate: payload.isCreate }
                    : { ...payload },
                at
            };
        }
        default:
            return null;
    }
};
const isRemoteWrite = (type) => type.endsWith('/hydrateFromRemote') || type.endsWith('/discardAllChanges');
const DISCARDED_KEY = 'jaenjs-state-discarded';
const DISCARDED_KEEP = 5;
const parkDiscardedOutbox = (outbox, discard) => {
    if (!outbox?.length)
        return;
    try {
        if (typeof localStorage === 'undefined')
            return;
        const held = JSON.parse(localStorage.getItem(DISCARDED_KEY) || '[]');
        const parks = Array.isArray(held) ? held : [];
        parks.push({
            revision: discard.revision,
            at: discard.at || new Date().toISOString(),
            by: discard.by || null,
            changes: outbox.map(entry => entry.change)
        });
        localStorage.setItem(DISCARDED_KEY, JSON.stringify(parks.slice(-DISCARDED_KEEP)));
    }
    catch (error) {
        console.error('jaen agent: the discarded changes could not be parked in this browser', error);
    }
};
const discardInvalidates = (remote, discardedRevision) => typeof discardedRevision === 'number' &&
    discardedRevision > 0 &&
    discardedRevision > (remote?.discardedRevision ?? 0);
const readMark = (remote) => typeof remote?.appliedRevision === 'number'
    ? remote.appliedRevision
    : typeof remote?.revision === 'number'
        ? remote.revision
        : undefined;
const knownMark = (remote) => {
    const applied = readMark(remote);
    const revision = typeof remote?.revision === 'number' ? remote.revision : undefined;
    if (typeof revision !== 'number')
        return applied;
    if (typeof applied !== 'number')
        return revision;
    return Math.max(revision, applied);
};
const applyDelta = (state, delta) => {
    if (!delta)
        return state;
    for (const [pageId, node] of Object.entries(delta.pages || {})) {
        if (!pageId || !node)
            continue;
        state.pages[pageId] = node;
    }
    const field = delta.mediaField;
    const media = delta.media || {};
    const removed = delta.removedMedia || [];
    if (field && (Object.keys(media).length > 0 || removed.length > 0)) {
        const page = (state.pages[field.pageId] = {
            ...(state.pages[field.pageId] || { id: field.pageId })
        });
        page.jaenFields = { ...(page.jaenFields || {}) };
        page.jaenFields[field.fieldType] = {
            ...(page.jaenFields[field.fieldType] || {})
        };
        const existing = page.jaenFields[field.fieldType][field.fieldName]?.value || {};
        const value = { ...existing };
        for (const [id, node] of Object.entries(media)) {
            if (!id)
                continue;
            value[id] = node;
        }
        for (const id of removed) {
            delete value[id];
        }
        page.jaenFields[field.fieldType][field.fieldName] = {
            ...(page.jaenFields[field.fieldType][field.fieldName] || {}),
            value
        };
    }
    if (delta.site) {
        state.site = { siteMetadata: delta.site.siteMetadata || {} };
    }
    for (const widget of delta.widgets || []) {
        if (!widget)
            continue;
        const at = state.widgets.findIndex((entry) => entry && entry.id && entry.id === widget.id);
        if (at >= 0) {
            state.widgets[at] = widget;
        }
        else {
            state.widgets.push(widget);
        }
    }
    return state;
};
export default (config) => {
    const recordMiddleware = (store) => (next) => (action) => {
        const stateBefore = action?.type === 'pages/field_write' ? store.getState() : undefined;
        const result = next(action);
        if (!action || typeof action.type !== 'string')
            return result;
        if (isRemoteWrite(action.type))
            return result;
        try {
            const change = recordAction(action, store.getState(), stateBefore);
            if (change) {
                store.dispatch(remoteActions.record(change));
            }
        }
        catch (error) {
            console.error('jaen agent: could not record an action', error);
        }
        return result;
    };
    const connect = (store) => {
        let flushTimer;
        let flushIsImmediate = false;
        let retryTimer;
        let pollTimer;
        let pollingEvery;
        let inFlight = false;
        let pollWanted = false;
        let failures = 0;
        let stopped = false;
        let socketLive = false;
        let closeSocket;
        let queuedSince;
        const state = () => store.getState();
        const clearFlush = () => {
            if (flushTimer)
                clearTimeout(flushTimer);
            flushTimer = undefined;
            flushIsImmediate = false;
        };
        const scheduleRetry = () => {
            if (retryTimer)
                clearTimeout(retryTimer);
            const seconds = RETRY_SECONDS[Math.min(Math.max(failures - 1, 0), RETRY_SECONDS.length - 1)] || 30;
            retryTimer = setTimeout(() => {
                retryTimer = undefined;
                void flush();
            }, seconds * 1000);
        };
        const flush = async () => {
            if (stopped || inFlight)
                return;
            const remote = state().remote;
            const batch = remote.outbox.slice(0, CALL_LIMIT);
            if (batch.length === 0)
                return;
            clearFlush();
            inFlight = true;
            store.dispatch(remoteActions.saveStarted());
            const ids = batch.map((entry) => entry.id);
            const changes = batch.map((entry) => entry.change);
            try {
                const answer = await saveChanges(config, changes, readMark(remote));
                failures = 0;
                store.dispatch(remoteActions.saveSucceeded({
                    ids,
                    revision: answer.revision,
                    savedAt: answer.savedAt || new Date().toISOString(),
                    rebased: Boolean(answer.rebased),
                    overwrote: answer.overwrote
                }));
                if (answer.rebased) {
                    wantPoll();
                }
            }
            catch (error) {
                if (error instanceof AgentError && error.code === 'DRAFT_DISCARDED') {
                    const now = state().remote;
                    const revision = error.details?.discardedRevision ||
                        (now.discardedRevision ?? 0) + 1;
                    parkDiscardedOutbox(now.outbox, {
                        revision,
                        at: error.details?.discardedAt,
                        by: error.details?.discardedByName
                    });
                    store.dispatch(remoteActions.draftDiscarded({
                        revision,
                        at: error.details?.discardedAt,
                        by: error.details?.discardedByName
                    }));
                    failures = 0;
                    inFlight = false;
                    void readWhole();
                    return;
                }
                failures += 1;
                store.dispatch(remoteActions.saveFailed({
                    offline: error instanceof AgentOfflineError,
                    message: error.message
                }));
                scheduleRetry();
            }
            finally {
                inFlight = false;
            }
            if (!stopped &&
                (state().remote.outbox.length || 0) > 0 &&
                failures === 0) {
                scheduleFlush(0);
            }
            if (!stopped && pollWanted && !inFlight) {
                void poll();
            }
        };
        const scheduleFlush = (delay) => {
            if (delay > 0 && flushTimer && flushIsImmediate)
                return;
            if (flushTimer)
                clearTimeout(flushTimer);
            flushIsImmediate = delay === 0;
            flushTimer = setTimeout(() => {
                flushTimer = undefined;
                flushIsImmediate = false;
                void flush();
            }, delay);
        };
        const quietDelay = () => {
            const window = config.debounceMs;
            if (queuedSince === undefined)
                return window;
            const left = queuedSince + MAX_WAIT_MS - Date.now();
            return Math.max(0, Math.min(window, left));
        };
        const hydrate = (answer) => {
            const current = state();
            const invalidated = discardInvalidates(current.remote, answer.discardedRevision);
            if (invalidated) {
                parkDiscardedOutbox(current.remote.outbox, {
                    revision: answer.discardedRevision,
                    at: answer.discardedAt,
                    by: answer.discardedByName || answer.discardedBy
                });
                store.dispatch(remoteActions.draftDiscarded({
                    revision: answer.discardedRevision,
                    publishedRevision: answer.publishedRevision,
                    at: answer.discardedAt,
                    by: answer.discardedByName || answer.discardedBy
                }));
            }
            const base = answer.full || invalidated
                ? emptyDraftState()
                : {
                    pages: { ...(current.page?.pages?.nodes || {}) },
                    site: {
                        siteMetadata: { ...(current.site?.siteMetadata || {}) }
                    },
                    widgets: [...(current.widget?.nodes || [])]
                };
            applyDelta(base, answer.delta);
            const outbox = (invalidated ? [] : current.remote.outbox);
            const merged = applyChanges(base, outbox.map(entry => entry.change));
            store.dispatch(pageActions.hydrateFromRemote({ nodes: merged.pages }));
            store.dispatch(siteActions.hydrateFromRemote(merged.site.siteMetadata));
            store.dispatch(widgetActions.hydrateFromRemote(merged.widgets));
            store.dispatch(remoteActions.remoteHydrated({
                revision: answer.revision,
                publishedRevision: answer.publishedRevision,
                authors: answer.delta?.authors || undefined,
                replaceAuthors: Boolean(answer.full)
            }));
        };
        const readWhole = async () => {
            if (stopped)
                return;
            try {
                const answer = await fetchDraft(config);
                if (answer.changed)
                    applyAnswer(answer);
            }
            catch (error) {
                console.debug('jaen agent: the whole draft could not be read', error);
            }
        };
        const applyAnswer = (answer, askedFrom) => {
            const remote = state().remote;
            const applied = readMark(remote);
            const known = knownMark(remote);
            if (typeof answer.revision !== 'number') {
                hydrate(answer);
                return;
            }
            if (typeof askedFrom === 'number' && answer.revision < askedFrom) {
                console.warn(`jaen agent: the draft went from revision ${askedFrom} back to ${answer.revision}, keeping what is in this browser`);
                store.dispatch(remoteActions.objectRestarted({
                    revision: answer.revision,
                    publishedRevision: answer.publishedRevision
                }));
                return;
            }
            if ((typeof applied === 'number' && answer.revision <= applied) ||
                (typeof known === 'number' && answer.revision < known)) {
                console.debug(`jaen agent: an answer at revision ${answer.revision} is older than this browser (applied ${applied}, known ${known}), keeping what is here`);
                store.dispatch(remoteActions.staleAnswerSkipped());
                return;
            }
            hydrate(answer);
        };
        const wantPoll = () => {
            if (stopped)
                return;
            if (inFlight) {
                pollWanted = true;
                return;
            }
            void poll();
        };
        const poll = async () => {
            if (stopped)
                return;
            if (inFlight) {
                pollWanted = true;
                return;
            }
            pollWanted = false;
            const askedFrom = readMark(state().remote);
            try {
                const answer = await fetchDraft(config, askedFrom);
                const remote = state().remote;
                if (discardInvalidates(remote, answer.discardedRevision)) {
                    parkDiscardedOutbox(remote.outbox, {
                        revision: answer.discardedRevision,
                        at: answer.discardedAt,
                        by: answer.discardedByName || answer.discardedBy
                    });
                    store.dispatch(remoteActions.draftDiscarded({
                        revision: answer.discardedRevision,
                        publishedRevision: answer.publishedRevision,
                        at: answer.discardedAt,
                        by: answer.discardedByName || answer.discardedBy
                    }));
                    if (!answer.changed) {
                        void readWhole();
                        return;
                    }
                }
                if (!answer.changed) {
                    if (typeof answer.revision === 'number' &&
                        answer.revision !== remote.revision) {
                        store.dispatch(remoteActions.revisionSeen({
                            revision: answer.revision,
                            publishedRevision: answer.publishedRevision
                        }));
                    }
                    return;
                }
                applyAnswer(answer, askedFrom);
            }
            catch (error) {
                console.debug('jaen agent: poll failed', error);
            }
        };
        const shouldPoll = () => {
            const s = state();
            return Boolean(s.remote?.active || s.status?.isEditing);
        };
        const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
        const wantedPollMs = () => {
            if (socketLive) {
                return SOCKET_SAFETY_POLL_MS;
            }
            return isVisible() ||
                inFlight ||
                (state().remote?.outbox?.length || 0) > 0
                ? config.activePollMs
                : config.pollMs;
        };
        const syncSocket = () => {
            if (stopped || !shouldPoll()) {
                if (closeSocket) {
                    closeSocket();
                    closeSocket = undefined;
                }
                if (socketLive) {
                    socketLive = false;
                    store.dispatch(remoteActions.connectionChanged('poll'));
                }
                return;
            }
            if (closeSocket)
                return;
            closeSocket = openDraftSocket(config, {
                onRevision: (revision, publishedRevision) => {
                    const applied = readMark(state().remote);
                    if (typeof applied === 'number' && revision <= applied) {
                        if (typeof publishedRevision === 'number') {
                            store.dispatch(remoteActions.revisionSeen({ revision, publishedRevision }));
                        }
                        return;
                    }
                    wantPoll();
                },
                onLive: live => {
                    socketLive = live;
                    store.dispatch(remoteActions.connectionChanged(live ? 'socket' : 'poll'));
                    syncPolling();
                    if (live)
                        void poll();
                }
            });
        };
        const syncPolling = () => {
            if (!shouldPoll()) {
                if (pollTimer)
                    clearInterval(pollTimer);
                pollTimer = undefined;
                pollingEvery = undefined;
                return;
            }
            const every = wantedPollMs();
            if (pollTimer && pollingEvery === every)
                return;
            const isFirst = !pollTimer;
            if (pollTimer)
                clearInterval(pollTimer);
            pollingEvery = every;
            pollTimer = setInterval(() => void poll(), every);
            if (isFirst)
                void poll();
        };
        const onVisibilityChange = () => {
            syncPolling();
            syncSocket();
            if (isVisible()) {
                void poll();
                return;
            }
            clearFlush();
            void flush();
        };
        const onPageHide = () => {
            clearFlush();
            void flush();
        };
        store.dispatch(remoteActions.resume());
        let lastOutboxLength = (state().remote?.outbox || []).length;
        const unsubscribe = store.subscribe(() => {
            const remote = state().remote;
            if (!remote)
                return;
            const length = remote.outbox.length;
            if (length === 0) {
                queuedSince = undefined;
            }
            else if (queuedSince === undefined) {
                queuedSince = Date.now();
            }
            if (length > lastOutboxLength) {
                const latest = remote.outbox[length - 1]?.change;
                scheduleFlush(length >= MAX_BATCH || !latest || goesAtOnce(latest)
                    ? 0
                    : quietDelay());
            }
            lastOutboxLength = length;
            syncPolling();
            syncSocket();
        });
        if (lastOutboxLength > 0) {
            scheduleFlush(0);
        }
        const onOnline = () => {
            failures = 0;
            scheduleFlush(0);
            void poll();
            syncSocket();
        };
        setLeaveOutboxFlush(() => {
            clearFlush();
            void flush();
        });
        if (typeof window !== 'undefined') {
            window.addEventListener('online', onOnline);
            window.addEventListener('pagehide', onPageHide);
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }
        syncPolling();
        syncSocket();
        return () => {
            stopped = true;
            setLeaveOutboxFlush(undefined);
            unsubscribe();
            clearFlush();
            if (retryTimer)
                clearTimeout(retryTimer);
            if (pollTimer)
                clearInterval(pollTimer);
            if (closeSocket) {
                closeSocket();
                closeSocket = undefined;
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('online', onOnline);
                window.removeEventListener('pagehide', onPageHide);
            }
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', onVisibilityChange);
            }
        };
    };
    return { recordMiddleware, connect, draftStateToData };
};
